<template>
  <div>
    <!-- Other content in your Marketplace component -->
    <v-pagination
      v-model="currentPage"
      :length="totalPages"
      @input="fetchNextPage"
      color="#4cd4ca"
    />
    <v-container style="height: 400px" v-if="loading">
      <v-row class="fill-height" align-content="center" justify="center">
        <v-col class="text-subtitle-1 text-center" cols="12">
          Fetching Race Cars...
        </v-col>
        <v-col cols="6">
          <v-progress-linear
            color="#4cd4ca"
            indeterminate
            rounded
            height="6"
          ></v-progress-linear>
        </v-col>
      </v-row>
    </v-container>
    <div v-if="vehicleList && !loading">
      <!-- Include the VehicleList component and pass the list of vehicles as a prop -->
      <VehicleList :vehicles="vehicleList" />

      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        @input="scrollToTopBeforeFetch"
        color="#4cd4ca"
      />
    </div>
  </div>
</template>

<script>
import VehicleList from "./VehicleList";
import axios from "axios";
import md5 from "js-md5"; // Import js-md5

export default {
  data() {
    return {
      vehicleList: [],
      exchangeRates: {},
      selectedVehicleId: null, // Store the selected vehicle for the modal
      currentPage: 1,
      pageSize: 20,
      totalPages: 1,
      loading: false, // Loading state
    };
  },
  created() {
    this.fetchData();
  },
  components: {
    VehicleList,
  },

  methods: {
    async fetchData() {
      try {
        this.loading = true;
        const authToken = await this.fetchAuthorizationToken();
        if (authToken) {
          await this.fetchExchangeRates(); // Fetch exchange rates after fetching vehicle data
          await this.fetchVehicleListings(authToken);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        this.loading = false;
      }
    },

    async scrollToTopBeforeFetch(newPage) {
      window.scrollTo(0, 0); // Scroll to the top of the page
      await this.fetchNextPage(newPage); // Fetch the next page of vehicle listings
    },

    async fetchAuthorizationToken() {
      const apiUrl = "https://feed.51gt3.com/api/v1/token";

      const requestData = {
        appId: "gtcollective",
        appSecret: "wh6q2ibl105cyu9v5cvp",
        scope: "cardata",
      };

      try {
        const response = await axios.post(apiUrl, requestData);
        return `Bearer ${response.data.data.token}`;
      } catch (error) {
        console.error("Failed to fetch authorization token", error);
        return null;
      }
    },

    async fetchExchangeRates() {
      const exchangeRatesApiUrl =
        "https://api.apilayer.com/exchangerates_data/latest?base=AUD&symbols=GBP,EUR,USD";

      try {
        const response = await axios.get(exchangeRatesApiUrl, {
          headers: {
            apikey: "BJArsENP0ERWrMlgx011YDetKYLNkcHZ",
          },
        });
        this.exchangeRates = response.data;
      } catch (error) {
        console.error("Failed to fetch exchange rates", error);
      }
    },

    convertPricesToAUD(data) {
      if (!this.exchangeRates || !this.exchangeRates.rates) {
        return data.records;
      }

      if (Array.isArray(data.records)) {
        return data.records.map((vehicle) => {
          const priceString = vehicle.price;

          // Check if the price is 'Sold' before attempting to parse as float
          if (priceString?.toLowerCase() === "sold") {
            return { ...vehicle, price: "SOLD" };
          }

          const price = parseFloat(priceString?.replace(/[^0-9.]/g, ""));

          if (isNaN(price)) {
            return { ...vehicle, price: "POA" };
          }

          const currencySymbol = vehicle.currency;
          const currencyCode = this.getCurrencyCodeFromSymbol(currencySymbol);

          if (currencyCode && this.exchangeRates.rates[currencyCode]) {
            // Convert the price to AUD
            let convertedPrice = price / this.exchangeRates.rates[currencyCode];

            // Add 5% for duty
            convertedPrice += 0.05 * convertedPrice;

            // Add 10% for GST (including duty)
            convertedPrice += 0.1 * convertedPrice;

            // Add $10,000 for shipping
            // convertedPrice += 10000;

            // Round the converted price to the nearest hundred
            convertedPrice = Math.round(convertedPrice / 100) * 100;

            return { ...vehicle, price: convertedPrice };
          } else {
            return { ...vehicle, price: "POA" };
          }
        });
      } else {
        return [];
      }
    },

    getCurrencyCodeFromSymbol(symbol) {
      // Define a mapping from symbols to currency codes
      const symbolToCode = {
        "£": "GBP", // British Pounds
        "€": "EUR", // Euros
        $: "USD", // Euros
        // Add more mappings as needed
      };

      // Use the mapping to get the currency code from the symbol
      return symbolToCode[symbol] || null;
    },

    async fetchNextPage(newPage) {
      // Fetch the next page of vehicle listings
      if (newPage > 0 && newPage <= this.totalPages) {
        this.currentPage = newPage;
        await this.fetchVehicleListings();
      }
    },

    async fetchVehicleListings() {
      const authToken = await this.fetchAuthorizationToken();
      if (authToken) {
        const apiUrl = "https://feed.51gt3.com/api/v1/feed/list";
        const currentTimestampMillis = Date.now();
        const requestData = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          nonce: "1111111111222222222233",
          timestamp: currentTimestampMillis.toString(),
        };

        const signature = md5(
          `gtcollectivewh6q2ibl105cyu9v5cvp1111111111222222222233${requestData.timestamp}`
        );
        requestData.signature = signature;

        try {
          const response = await axios.post(apiUrl, requestData, {
            headers: {
              Authorization: authToken,
              "User-Agent": "apifox/1.0.0 (https://www.apifox.cn)",
              "Content-Type": "application/json",
              Accept: "*/*",
              Host: "feed.51gt3.com",
              Connection: "keep-alive",
            },
          });

          this.totalPages = Math.ceil(response.data.data.total / this.pageSize);
          this.vehicleList = this.convertPricesToAUD(response.data.data);
        } catch (error) {
          console.error("Failed to fetch vehicle listings", error);
        }
      }
    },
  },
};
</script>
